import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import groupBy from 'lodash.groupby';
import { ArrowLeft } from './icons';
import ResearchItem from './components/ResearchItem';
import { Document } from './types';
import axios from 'axios';
import { Loader } from 'semantic-ui-react';
import { useDocuments } from '../../DocumentsContext';
import sortBy from 'lodash.sortby';

const Wrapper = styled.div<{ width: number }>`
  position: fixed;
  width: ${(props: any) => props.width + 'px'};
  right: 0;
  top: 0;
  height: 100vh; /* viewport height */
  z-index: 600;
  bottom: 0;
  transition: width 0.2s;
  background-color: #ffffff;
  filter: drop-shadow(0px 16px 32px rgba(190, 192, 199, 0.51));
`;

const Toggle = styled.div`
  align-items: center;
  background: #fff5f5;
  color: #444444;
  display: flex;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: bold;
  height: 48px;
  justify-content: center;
  left: -144px;
  padding: 8px;
  position: absolute;
  top: 218px;
  transform: rotate(-90deg);
  width: 240px;
  cursor: pointer;
  border-radius: 5px 5px 0 0;
`;

const InnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
`;
const Arrow = styled.div<{ isOpened: boolean }>`
  margin-right: 16px;
  transform: ${(props: any) => (props.isOpened ? 'rotate(-90deg)' : 'rotate(90deg)')};
`;

const TopSection = styled.div`
  padding: 40px;
  width: 100%;
  border-bottom: 1px solid #8e8e93;
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 30px;
  color: #333333;
`;

const ExtractionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 40px;
  border-bottom: 1px solid #d1d1d6;
  font-family: Lato;
  font-size: 13px;
`;

const ExtractButton = styled.button`
  border: 0;
  border-radius: 4px;
  background: #ff5252;
  color: white;   
  padding: 8px 12px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
  justify-content: center;
  &:disabled { opacity: 0.55; cursor: default; }
`;

const ButtonSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
  flex-shrink: 0;
`;


const NumberOfSources = styled.div`
  position: absolute;
  top: -4px;
  right: -8px;
  color: #fff;
  border-radius: 50%;
  background-color: #ff5252;
  width: 21px;
  height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border: 2px solid #fff;
  transform: rotate(90deg);
`;

const documents: Document[] = [
  {
    name: 'document1',
    sourceUrl: 'google.com',
    documentId: 2105,
    searchId: 123,
    documentSearchesId: 1244,
    numberOfPages: 4,
    automationType: 'NYC: Emergency Repair',
    raSortOrder: 1,
    dateUploaded: new Date(),
  },
  {
    name: 'testDocument2',
    sourceUrl: 'google.com',
    documentId: 2105,
    searchId: 345,
    documentSearchesId: 765,
    numberOfPages: 5,
    automationType: 'NYC: Housing Building',
    raSortOrder: 2,
    dateUploaded: new Date(),
  },
  {
    name: 'document3',
    sourceUrl: 'tetsturl.com',
    documentId: 2105,
    searchId: 123,
    documentSearchesId: null,
    numberOfPages: 2,
    automationType: 'NYC: EGOV-Tax',
    raSortOrder: 3,
    dateUploaded: new Date(),
  },
  {
    name: 'document4',
    sourceUrl: 'anotherurl.com',
    documentId: 2105,
    searchId: 123,
    documentSearchesId: null,
    numberOfPages: 1,
    automationType: 'Previous Files',
    raSortOrder: 4,
    dateUploaded: new Date(),
  },
];

interface PropTypes {
  searchId: number;
  propertyId?: number;
  isNyc?: boolean;
  isTaxSearch?: boolean;
}

const HelperDocuments = (props: PropTypes) => {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [sourceOfDocs, setSourceOfDocs] = useState<{ [str: string]: Document[] }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]);
  const [extracting, setExtracting] = useState<boolean>(false);
  const [extractionMessage, setExtractionMessage] = useState<string>('');

  const { documents } = useDocuments();

  const getDocuments = async (searchId: number) => {
    setLoading(true);
    if (props.propertyId === null || props.propertyId === undefined) {
      const { data: orderDocData } = await axios.get<Document[]>(
        `/api/helperDocuments/GetOrderDocumentsAsHelperDocuments/${props.searchId}`
      );
      const { data: previousSearches } = await axios.get<Document[]>(
        `/api/helperDocuments/getPreviousSearchAsHelperDocuments/${props.searchId}`
      );
      const { data } = await axios.get<Document[]>(
        `/api/helperDocuments/getHelperDocuments/${props.searchId}`
      );

      if (data.length > 0 && props.isNyc) {
        setSourceOfDocs(
          sortAndGroupDocuments(
            orderDocData.concat(previousSearches).concat(data.map(d => ({ ...d, isHelperDocument: true })))
          )
        );
      } else {
        const { data: searchDocsData } = await axios.get<Document[]>(
          `/api/helperDocuments/getSearchDocsAsHelperDocuments/${props.searchId}`
        );

        setSourceOfDocs(sortAndGroupDocuments(orderDocData.concat(previousSearches).concat(searchDocsData)));
      }
      setLoading(false);
    } else {
      axios
        .get(`/api/helperDocuments/getHelperDocumentsForProperty/${props.propertyId}/${props.searchId}`)
        .then(({ data }) => {
          setSourceOfDocs(sortAndGroupDocuments(data));
        })
        .finally(() => setLoading(false));
    }
  };
  useEffect(() => {
    if (props.searchId || props.propertyId) {
      getDocuments(props.searchId);
    }
  }, [props.searchId, props.propertyId, JSON.stringify(documents)]);

  const sortAndGroupDocuments = (documents: Document[]) => {
    const sorted = sortBy(documents, 'rasortOrder');
    const grouped = groupBy(sorted, 'sourceUrl');
    return grouped;
  };

  const toggleDocument = (documentId: number) => {
    setSelectedDocumentIds(selected =>
      selected.includes(documentId) ? selected.filter(id => id !== documentId) : [...selected, documentId]
    );
  };

  const runTaxExtraction = async () => {
    if ((!props.propertyId && !props.searchId) || selectedDocumentIds.length === 0) return;
    setExtracting(true);
    setExtractionMessage('');
    try {
      const { data } = await axios.post('/api/orders/DirectApplyNysTaxExtraction', {
        propertyId: props.propertyId,
        searchId: props.searchId,
        documentIds: selectedDocumentIds,
      });
      if (data?.feedbackId) {
        sessionStorage.setItem('taxExtractionFeedbackId', JSON.stringify({ propertyId: props.propertyId, feedbackId: data.feedbackId }));
      }
      window.location.reload();
    } catch (error) {
      setExtractionMessage((error as any)?.response?.data || 'Could not run tax extraction.');
    } finally {
      setExtracting(false);
    }
  };

  if (Object.keys(sourceOfDocs).length === 0) {
    return <div />;
  }
  return (
    <Wrapper width={isOpened ? 575 : 0}>
      {loading && <Loader active size="large" />}
      <Toggle onClick={() => setIsOpened(io => !io)}>
        <Arrow isOpened={isOpened}>
          <ArrowLeft />
        </Arrow>
        Research Assistant
        <NumberOfSources>{Object.keys(sourceOfDocs).length}</NumberOfSources>
      </Toggle>
      <InnerWrapper>
        <TopSection>Look What We Found!</TopSection>
        {props.isTaxSearch && !props.isNyc && (
          <ExtractionBar>
            <div>
              {extractionMessage || `${selectedDocumentIds.length} document${selectedDocumentIds.length === 1 ? '' : 's'} selected for NYS tax auto-fill`}
            </div>
            <ExtractButton disabled={extracting || selectedDocumentIds.length === 0} onClick={runTaxExtraction}>
              {extracting ? <><ButtonSpinner />Reading…</> : 'Auto-fill Tax Form'}
            </ExtractButton>
          </ExtractionBar>
        )}
        {Object.keys(sourceOfDocs).map(k => {
          return (
            <ResearchItem
              getDocuments={getDocuments}
              source={k}
              documents={sourceOfDocs[k]}
              selectedDocumentIds={selectedDocumentIds}
              onToggleDocument={props.isTaxSearch && !props.isNyc ? toggleDocument : undefined}
              isNyc={props.isNyc}
            />
          );
        })}
      </InnerWrapper>
    </Wrapper>
  );
};

export default HelperDocuments;
